import {
  ContentFeedNode,
  ProductMatchingOperator,
  ProductMatchingOperatorDescription,
} from 'common/src/modules/decisionTree/decisionTree.types';
import {ContentSelectionStrategyValue} from 'common/src/modules/contentFeeds/contentFeeds.enums';
import {OutputType} from 'common/src/modules/nodes/nodes.types';
import getMatchingEnumValue from 'common/src/utils/typescriptUtils/getMatchingEnumValue';
import {
  formatOutputHeader,
  getBaseHeader,
  getCategoryUINamePart,
  getDestinationPhrase,
  getOutputTypePhrase,
  sortOutputsByType,
} from './utils';

function getContentOutputWhenPhrase(output: {type?: number}): string {
  switch (output.type) {
    case OutputType.Regular:
    case OutputType.Last:
      return 'when content item selected';
    case OutputType.Fallback:
    default:
      return 'when no content item';
  }
}

export function contentCardToText(
  card: ContentFeedNode,
  mode: 'short' | 'full',
  fallbackCreativeNodeId?: number,
): string {
  const header = getBaseHeader(card);
  const sortedOutputs = sortOutputsByType(card.outputs ?? []);

  if (mode === 'short') {
    let purposeLine = '';

    if (
      card.strategy === ContentSelectionStrategyValue.Matching &&
      card.configuration?.conditions?.length
    ) {
      const signalNames = card.configuration.conditions
        .map(
          (condition) =>
            `${getCategoryUINamePart(condition.signalMainCategory)}${condition.feedColumnUIName ?? condition.feedColumnSignalKey}`,
        )
        .join(' and ');

      purposeLine = `Purpose: Match content by ${signalNames}`;
    } else if (card.strategy === ContentSelectionStrategyValue.Specific && card.contentId) {
      purposeLine = `Purpose: Select content item ${card.contentId}`;
    }

    const outputLines = sortedOutputs.map((output, index) => {
      const outputHeader = formatOutputHeader(index, output);
      const outputType = getOutputTypePhrase(output);
      const dest = getDestinationPhrase(output.nodeId, fallbackCreativeNodeId, output.type);

      return `${outputHeader} ${outputType} ${dest}`;
    });

    return [header, purposeLine, ...outputLines].filter(Boolean).join('\n');
  }

  const isMatchingStrategy = card.strategy === ContentSelectionStrategyValue.Matching;
  const isSpecificContent = card.strategy === ContentSelectionStrategyValue.Specific;

  const contentFeedLine = card.contentFeed
    ? `Content Feed '${card.contentFeed.name}' ID ${card.contentFeed.id}`
    : 'Content Feed not set';
  const contentSetLine = card.contentSet
    ? `Content Set '${card.contentSet.name}' ID ${card.contentSet.id}`
    : 'Content Set not set';
  const specificContentIdLine =
    isSpecificContent && card.contentId
      ? `Specific Content Id ${card.contentId}`
      : isSpecificContent
        ? 'Specific Content Id not set'
        : null;

  const matchingStrategyConditionsLines =
    isMatchingStrategy && card.configuration?.conditions?.length
      ? card.configuration.conditions.map((c) => {
          return `Content ${c.feedColumnUIName} (Signal Key: ${c.feedColumnSignalKey}) ${getMatchingEnumValue(ProductMatchingOperator, ProductMatchingOperatorDescription, c.operator)} ${getCategoryUINamePart(c.signalMainCategory)}${c.signalUIName} (Signal Key: ${c.signalKey})`;
        })
      : [];
  const matchingStrategyLines =
    isMatchingStrategy && card.configuration?.conditions?.length
      ? ['Matching Strategy Conditions:', ...matchingStrategyConditionsLines]
      : isMatchingStrategy
        ? ['Matching Strategy Conditions: not set']
        : [];

  const outputLines = sortedOutputs.map((output, index) => {
    const outputHeader = formatOutputHeader(index, output);
    const dest = getDestinationPhrase(output.nodeId, fallbackCreativeNodeId, output.type);
    const whenPhrase = getContentOutputWhenPhrase(output);

    return `${outputHeader} ${dest} ${whenPhrase}`;
  });

  const lines = [
    header,
    contentFeedLine,
    contentSetLine,
    specificContentIdLine,
    ...matchingStrategyLines,
    ...outputLines,
  ].filter(Boolean);

  return lines.join('\n');
}
